import { ReactNode, useEffect } from "react";
import { initializeApp } from "firebase/app";
import { FeatureFlagContext } from "./FeatureFlagContext.ts";
import { FeatureFlagManager } from "./FeatureFlagManager.ts";
import { initialState } from "./initialState";
import { createLocalStorageRepository } from "./LocalStorageRepository";
import { FirebaseRemoteConfigAdapter } from "./FirebaseRemoteConfigAdapter";
import { config } from "../config.ts";
import { getEnvironment } from "../getEnvironment.ts";

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  // Initialize Firebase App for Remote Config
  const environment = getEnvironment();
  let remoteConfigAdapter: FirebaseRemoteConfigAdapter | undefined;

  try {
    const firebaseApp = initializeApp(config.firebase[environment]);
    remoteConfigAdapter = new FirebaseRemoteConfigAdapter(firebaseApp);
  } catch (error) {
    console.warn("Failed to initialize Firebase Remote Config:", error);
    remoteConfigAdapter = undefined;
  }

  const featureFlagManager: FeatureFlagManager | null = new FeatureFlagManager(
    initialState,
    createLocalStorageRepository(),
    remoteConfigAdapter,
  );

  // Initialize remote config asynchronously
  useEffect(() => {
    if (!remoteConfigAdapter) return;

    const initRemoteConfig = async () => {
      try {
        await featureFlagManager.initializeRemoteConfig();
        console.info("Firebase Remote Config initialized for feature flags");
      } catch (error) {
        console.warn("Failed to initialize Firebase Remote Config:", error);
      }
    };

    initRemoteConfig();
  }, [featureFlagManager, remoteConfigAdapter]);

  if (typeof window !== "undefined") {
    updateFeatureFlagsFromUrl(featureFlagManager);
    console.info(
      `To enable the Labs UI, set the "enableLabs" feature flag to true by running:
      window.featureFlagManager.setConfig("enableLabs", true)
      Or use URL parameter: ?enableLabs=1`,
    );
    window.featureFlagManager = featureFlagManager;
  }

  return (
    <FeatureFlagContext.Provider
      value={{
        featureFlagManager: featureFlagManager,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

function updateFeatureFlagsFromUrl(
  featureFlagManager: FeatureFlagManager,
): void {
  const urlParams = new URLSearchParams(window.location.search);
  const flags = Object.keys(featureFlagManager.getState());
  flags.forEach((flag) => {
    if (urlParams.has(flag)) {
      const value = urlParams.get(flag);
      if (value === "1" || value === "true") {
        featureFlagManager.setConfig(flag as keyof typeof initialState, true);
        console.info(`${flag} enabled via URL parameter`);
      } else if (value === "0" || value === "false") {
        featureFlagManager.setConfig(flag as keyof typeof initialState, false);
        console.info(`${flag} disabled via URL parameter`);
      }
    }
  });
}
